/**
 * Top-level ISO-BMFF box walker for a fragmented MP4 byte stream.
 *
 * The muxer's write callbacks are not trusted for segment boundaries: bytes are accumulated and
 * cut only on complete top-level boxes. Everything before and including `moov` is the init
 * segment; each `moof` plus the `mdat` that follows it is one media segment. Other top-level boxes
 * between fragments (`mfra`, `free`, `skip`, `styp`, `sidx`, `prft`) are dropped so every media
 * segment starts with `moof`.
 */

export interface Fmp4SplitterHandlers {
  onInit(bytes: Uint8Array): void;
  onFragment(bytes: Uint8Array): void;
}

export interface Fmp4Splitter {
  /** Appends bytes in stream order. */
  push(bytes: Uint8Array): void;
  /** Bytes received but not yet part of a complete top-level box. */
  readonly pending: number;
}

interface BoxHeader {
  type: string;
  size: number;
  headerSize: number;
}

function readType(b: Uint8Array, at: number): string {
  return String.fromCharCode(b[at]!, b[at + 1]!, b[at + 2]!, b[at + 3]!);
}

function readU32(b: Uint8Array, at: number): number {
  return ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
}

function readU64(b: Uint8Array, at: number): number {
  return readU32(b, at) * 2 ** 32 + readU32(b, at + 4);
}

/** Reads the header of the box at `at`, or null when not enough bytes are available yet. */
function readBoxHeader(b: Uint8Array, at: number, end: number): BoxHeader | null {
  if (end - at < 8) return null;
  let size = readU32(b, at);
  const type = readType(b, at + 4);
  let headerSize = 8;
  if (size === 1) {
    if (end - at < 16) return null;
    size = readU64(b, at + 8);
    headerSize = 16;
  } else if (size === 0) {
    throw new Error(`fmp4-split: box '${type}' extends to end of stream, which a live stream cannot have`);
  }
  if (size < headerSize) throw new Error(`fmp4-split: invalid size ${size} for box '${type}'`);
  return { type, size, headerSize };
}

/**
 * FIFO of byte chunks. Bytes are copied once, when a complete box is taken out, so splitting is
 * linear in the stream size however the muxer chunks its writes.
 */
class ByteQueue {
  private chunks: Uint8Array[] = [];
  private head = 0; // bytes of chunks[0] already consumed
  length = 0;

  push(bytes: Uint8Array): void {
    this.chunks.push(bytes);
    this.length += bytes.length;
  }

  /** The first `n` bytes (fewer if not available), without consuming them. */
  peek(n: number): Uint8Array {
    n = Math.min(n, this.length);
    const first = this.chunks[0];
    if (first && first.length - this.head >= n) return first.subarray(this.head, this.head + n);
    return this.copy(n);
  }

  /** Removes and returns a copy of the first `n` bytes (n <= length). */
  take(n: number): Uint8Array {
    const out = this.copy(n);
    this.skip(n);
    return out;
  }

  skip(n: number): void {
    this.length -= n;
    while (n > 0) {
      const first = this.chunks[0]!;
      const avail = first.length - this.head;
      if (avail > n) {
        this.head += n;
        return;
      }
      n -= avail;
      this.chunks.shift();
      this.head = 0;
    }
  }

  private copy(n: number): Uint8Array {
    const out = new Uint8Array(n);
    let o = 0;
    let head = this.head;
    for (const c of this.chunks) {
      if (o >= n) break;
      const part = c.subarray(head, Math.min(c.length, head + (n - o)));
      out.set(part, o);
      o += part.length;
      head = 0;
    }
    return out;
  }
}

export function createFmp4Splitter(handlers: Fmp4SplitterHandlers): Fmp4Splitter {
  const q = new ByteQueue();
  let initDone = false;
  let initEnd = 0; // leading bytes of q that belong to the init segment so far

  /** Header of the box starting `at` bytes into the queue, or null when not all of it is there. */
  function header(at: number): BoxHeader | null {
    const b = q.peek(at + 16);
    return readBoxHeader(b, at, b.length);
  }

  function walk(): void {
    for (;;) {
      if (!initDone) {
        const h = header(initEnd);
        if (!h || q.length - initEnd < h.size) return;
        if (h.type === 'moof' || h.type === 'mdat') throw new Error(`fmp4-split: '${h.type}' before 'moov'`);
        initEnd += h.size;
        if (h.type === 'moov') {
          initDone = true;
          handlers.onInit(q.take(initEnd));
        }
        continue;
      }
      // After the init segment, the next unconsumed box always starts at the head of the queue.
      const h = header(0);
      if (!h || q.length < h.size) return;
      if (h.type !== 'moof') {
        if (h.type === 'mdat') throw new Error("fmp4-split: 'mdat' without a preceding 'moof'");
        q.skip(h.size); // not part of any segment (mfra, free, styp, ...)
        continue;
      }
      const m = header(h.size);
      if (!m || q.length - h.size < m.size) return;
      if (m.type !== 'mdat') throw new Error(`fmp4-split: expected 'mdat' after 'moof', got '${m.type}'`);
      handlers.onFragment(q.take(h.size + m.size));
    }
  }

  return {
    push(bytes) {
      if (bytes.length === 0) return;
      q.push(bytes);
      walk();
    },
    get pending() {
      return q.length;
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Minimal parsers used to annotate segments (timing, sync samples) without trusting the muxer.

export interface InitTrackInfo {
  trackId: number;
  handler: string; // 'vide' | 'soun' | ...
  timescale: number;
  defaultSampleDuration: number;
  defaultSampleFlags: number;
}

export interface FragmentTrackInfo {
  trackId: number;
  baseMediaDecodeTime: number;
  sampleCount: number;
  /** Sum of sample durations in track timescale units. */
  duration: number;
  /** Flags of the first sample (ISO/IEC 14496-12 sample_flags). */
  firstSampleFlags: number;
}

/** A box inside a byte buffer: `start` is its header, `body` its payload, `end` one past its last byte. */
export interface BoxRange {
  type: string;
  start: number;
  body: number;
  end: number;
}

/** The complete boxes laid end to end in `b[start, end)`; stops at the first truncated one. */
export function children(b: Uint8Array, start: number, end: number): BoxRange[] {
  const out: BoxRange[] = [];
  let at = start;
  while (at + 8 <= end) {
    const h = readBoxHeader(b, at, end);
    if (!h || at + h.size > end) break;
    out.push({ type: h.type, start: at, body: at + h.headerSize, end: at + h.size });
    at += h.size;
  }
  return out;
}

/** The first box at `path` (e.g. `['moof', 'traf', 'tfdt']`) under `b[start, end)`, or null. */
export function find(b: Uint8Array, start: number, end: number, path: string[]): { body: number; end: number } | null {
  let range = { body: start, end };
  for (const type of path) {
    const next = children(b, range.body, range.end).find((c) => c.type === type);
    if (!next) return null;
    range = next;
  }
  return range;
}

/** Sample is a sync sample when `sample_is_non_sync_sample` (bit 16) is clear. */
export function isSyncSampleFlags(flags: number): boolean {
  return (flags & 0x0001_0000) === 0;
}

export function parseInit(init: Uint8Array): InitTrackInfo[] {
  const moov = find(init, 0, init.length, ['moov']);
  if (!moov) throw new Error('parseInit: no moov');
  const trex = new Map<number, { duration: number; flags: number }>();
  const mvex = find(init, moov.body, moov.end, ['mvex']);
  if (mvex) {
    for (const c of children(init, mvex.body, mvex.end)) {
      if (c.type !== 'trex') continue;
      // version/flags(4) track_ID(4) default_sample_description_index(4) duration(4) size(4) flags(4)
      trex.set(readU32(init, c.body + 4), { duration: readU32(init, c.body + 12), flags: readU32(init, c.body + 20) });
    }
  }
  const tracks: InitTrackInfo[] = [];
  for (const trak of children(init, moov.body, moov.end).filter((c) => c.type === 'trak')) {
    const tkhd = find(init, trak.body, trak.end, ['tkhd']);
    const mdhd = find(init, trak.body, trak.end, ['mdia', 'mdhd']);
    const hdlr = find(init, trak.body, trak.end, ['mdia', 'hdlr']);
    if (!tkhd || !mdhd || !hdlr) continue;
    const tkhdV = init[tkhd.body]!;
    const trackId = readU32(init, tkhd.body + (tkhdV === 1 ? 20 : 12));
    const mdhdV = init[mdhd.body]!;
    const timescale = readU32(init, mdhd.body + (mdhdV === 1 ? 20 : 12));
    const handler = readType(init, hdlr.body + 8);
    const d = trex.get(trackId);
    tracks.push({ trackId, handler, timescale, defaultSampleDuration: d?.duration ?? 0, defaultSampleFlags: d?.flags ?? 0 });
  }
  return tracks;
}

export function parseFragment(fragment: Uint8Array, init: InitTrackInfo[]): FragmentTrackInfo[] {
  const moof = find(fragment, 0, fragment.length, ['moof']);
  if (!moof) throw new Error('parseFragment: no moof');
  const out: FragmentTrackInfo[] = [];
  for (const traf of children(fragment, moof.body, moof.end).filter((c) => c.type === 'traf')) {
    const tfhd = find(fragment, traf.body, traf.end, ['tfhd']);
    if (!tfhd) continue;
    const tfhdFlags = readU32(fragment, tfhd.body) & 0xffffff;
    const trackId = readU32(fragment, tfhd.body + 4);
    const defaults = init.find((t) => t.trackId === trackId);
    let defaultDuration = defaults?.defaultSampleDuration ?? 0;
    let defaultFlags = defaults?.defaultSampleFlags ?? 0;
    let p = tfhd.body + 8;
    if (tfhdFlags & 0x01) p += 8; // base_data_offset
    if (tfhdFlags & 0x02) p += 4; // sample_description_index
    if (tfhdFlags & 0x08) { defaultDuration = readU32(fragment, p); p += 4; }
    if (tfhdFlags & 0x10) p += 4; // default_sample_size
    if (tfhdFlags & 0x20) { defaultFlags = readU32(fragment, p); p += 4; }

    const tfdt = find(fragment, traf.body, traf.end, ['tfdt']);
    let baseMediaDecodeTime = 0;
    if (tfdt) {
      baseMediaDecodeTime = fragment[tfdt.body] === 1 ? readU64(fragment, tfdt.body + 4) : readU32(fragment, tfdt.body + 4);
    }

    let sampleCount = 0;
    let duration = 0;
    let firstSampleFlags: number | null = null;
    for (const trun of children(fragment, traf.body, traf.end).filter((c) => c.type === 'trun')) {
      const flags = readU32(fragment, trun.body) & 0xffffff;
      const count = readU32(fragment, trun.body + 4);
      let q = trun.body + 8;
      if (flags & 0x001) q += 4; // data_offset
      let firstFlagsOverride: number | null = null;
      if (flags & 0x004) { firstFlagsOverride = readU32(fragment, q); q += 4; }
      for (let i = 0; i < count; i++) {
        let sDuration = defaultDuration;
        let sFlags = defaultFlags;
        if (flags & 0x100) { sDuration = readU32(fragment, q); q += 4; }
        if (flags & 0x200) q += 4;
        if (flags & 0x400) { sFlags = readU32(fragment, q); q += 4; }
        if (flags & 0x800) q += 4;
        if (i === 0 && firstFlagsOverride !== null) sFlags = firstFlagsOverride;
        if (firstSampleFlags === null) firstSampleFlags = sFlags;
        duration += sDuration;
      }
      sampleCount += count;
    }
    out.push({ trackId, baseMediaDecodeTime, sampleCount, duration, firstSampleFlags: firstSampleFlags ?? defaultFlags });
  }
  return out;
}

/**
 * Adds `offsetUs` to every track's `tfdt` (baseMediaDecodeTime) of a fragment, in place, using each
 * track's timescale from the init. Used to keep the timeline continuous when a new muxer (which
 * always starts at 0) takes over mid-stream.
 */
export function shiftFragmentDecodeTime(fragment: Uint8Array, init: InitTrackInfo[], offsetUs: number): void {
  if (offsetUs === 0) return;
  const moof = find(fragment, 0, fragment.length, ['moof']);
  if (!moof) throw new Error('shiftFragmentDecodeTime: no moof');
  for (const traf of children(fragment, moof.body, moof.end).filter((c) => c.type === 'traf')) {
    const tfhd = find(fragment, traf.body, traf.end, ['tfhd']);
    const tfdt = find(fragment, traf.body, traf.end, ['tfdt']);
    if (!tfhd || !tfdt) continue;
    const trackId = readU32(fragment, tfhd.body + 4);
    const track = init.find((t) => t.trackId === trackId);
    if (!track) continue;
    const delta = Math.round((offsetUs * track.timescale) / 1e6);
    const at = tfdt.body + 4;
    if (fragment[tfdt.body] === 1) {
      const v = readU64(fragment, at) + delta;
      writeU32(fragment, at, Math.floor(v / 2 ** 32));
      writeU32(fragment, at + 4, v % 2 ** 32);
    } else {
      const v = readU32(fragment, at) + delta;
      if (v >= 2 ** 32) throw new Error('shiftFragmentDecodeTime: 32-bit tfdt overflow');
      writeU32(fragment, at, v);
    }
  }
}

function writeU32(b: Uint8Array, at: number, v: number): void {
  b[at] = (v >>> 24) & 255;
  b[at + 1] = (v >>> 16) & 255;
  b[at + 2] = (v >>> 8) & 255;
  b[at + 3] = v & 255;
}
